import React, { useState, useCallback } from 'react';
import { Copy, Download, RefreshCw, Code, Settings } from 'lucide-react';

interface PythonFormatterProps {}

const PythonFormatter: React.FC<PythonFormatterProps> = () => {
  const [inputCode, setInputCode] = useState<string>('');
  const [formattedCode, setFormattedCode] = useState<string>('');
  const [indentSize, setIndentSize] = useState<number>(4);
  const [maxLineLength, setMaxLineLength] = useState<number>(88);
  const [isFormatting, setIsFormatting] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);

  const pythonKeywords = [
    'False', 'None', 'True', 'and', 'as', 'assert', 'async', 'await', 'break', 'class',
    'continue', 'def', 'del', 'elif', 'else', 'except', 'finally', 'for', 'from',
    'global', 'if', 'import', 'in', 'is', 'lambda', 'nonlocal', 'not', 'or', 'pass',
    'raise', 'return', 'try', 'while', 'with', 'yield'
  ];

  const builtinFunctions = [
    'abs', 'all', 'any', 'ascii', 'bin', 'bool', 'bytearray', 'bytes', 'callable',
    'chr', 'classmethod', 'compile', 'complex', 'delattr', 'dict', 'dir', 'divmod',
    'enumerate', 'eval', 'exec', 'filter', 'float', 'format', 'frozenset', 'getattr',
    'globals', 'hasattr', 'hash', 'help', 'hex', 'id', 'input', 'int', 'isinstance',
    'issubclass', 'iter', 'len', 'list', 'locals', 'map', 'max', 'memoryview', 'min',
    'next', 'object', 'oct', 'open', 'ord', 'pow', 'print', 'property', 'range',
    'repr', 'reversed', 'round', 'set', 'setattr', 'slice', 'sorted', 'staticmethod',
    'str', 'sum', 'super', 'tuple', 'type', 'vars', 'zip'
  ];

  const formatPython = useCallback((code: string): string => {
    if (!code.trim()) return '';

    let formatted = code;
    const indent = ' '.repeat(indentSize);
    
    // Split into lines
    const lines = formatted.split('\n');
    const formattedLines: string[] = [];
    let currentIndentLevel = 0;
    let inMultilineString = false;
    let stringDelimiter = '';

    for (let i = 0; i < lines.length; i++) {
      let line = lines[i];
      const trimmedLine = line.trim();
      
      // Skip empty lines but preserve them
      if (!trimmedLine) {
        formattedLines.push('');
        continue;
      }

      // Handle multiline strings
      if (!inMultilineString) {
        if (trimmedLine.includes('"""') || trimmedLine.includes("'''")) {
          const tripleQuoteMatch = trimmedLine.match(/"""|\'\'\'/);
          if (tripleQuoteMatch) {
            stringDelimiter = tripleQuoteMatch[0];
            const quoteCount = (trimmedLine.match(new RegExp(stringDelimiter, 'g')) || []).length;
            if (quoteCount === 1) {
              inMultilineString = true;
            }
          }
        }
      } else {
        if (trimmedLine.includes(stringDelimiter)) {
          inMultilineString = false;
          stringDelimiter = '';
        }
      }

      // Don't format inside multiline strings
      if (inMultilineString && i > 0) {
        formattedLines.push(line);
        continue;
      }

      // Handle dedent for certain keywords
      if (trimmedLine.match(/^(elif|else|except|finally|case|default)[\s:]/)) {
        currentIndentLevel = Math.max(0, currentIndentLevel - 1);
      }

      // Apply indentation
      const indentedLine = indent.repeat(currentIndentLevel) + trimmedLine;
      
      // Handle line length
      if (indentedLine.length > maxLineLength && !trimmedLine.startsWith('#')) {
        // Simple line breaking for long lines (basic implementation)
        if (trimmedLine.includes(',') && !trimmedLine.includes('"""') && !trimmedLine.includes("'''")) {
          const parts = trimmedLine.split(',');
          if (parts.length > 1) {
            formattedLines.push(indent.repeat(currentIndentLevel) + parts[0] + ',');
            for (let j = 1; j < parts.length - 1; j++) {
              formattedLines.push(indent.repeat(currentIndentLevel + 1) + parts[j].trim() + ',');
            }
            const lastPart = parts[parts.length - 1].trim();
            if (lastPart) {
              formattedLines.push(indent.repeat(currentIndentLevel + 1) + lastPart);
            }
          } else {
            formattedLines.push(indentedLine);
          }
        } else {
          formattedLines.push(indentedLine);
        }
      } else {
        formattedLines.push(indentedLine);
      }

      // Handle indent for next line
      if (trimmedLine.endsWith(':') && !trimmedLine.startsWith('#')) {
        currentIndentLevel++;
      }
      
      // Handle dedent after certain statements
      if (trimmedLine.match(/^(break|continue|pass|return|raise)(\s|$)/) && 
          !trimmedLine.endsWith(':')) {
        // These don't affect indentation for next line
      }
    }

    return formattedLines
      .map(line => line.trimEnd()) // Remove trailing spaces
      .join('\n')
      .replace(/\n{3,}/g, '\n\n'); // Limit consecutive empty lines to 2
  }, [indentSize, maxLineLength]);

  const handleFormat = useCallback(async () => {
    if (!inputCode.trim()) return;
    
    setIsFormatting(true);
    
    // Simulate formatting delay for better UX
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const formatted = formatPython(inputCode);
    setFormattedCode(formatted);
    setIsFormatting(false);
  }, [inputCode, formatPython]);

  const handleCopy = useCallback(async () => {
    if (!formattedCode) return;
    
    try {
      await navigator.clipboard.writeText(formattedCode);
      const button = document.getElementById('copy-button');
      if (button) {
        const originalText = button.textContent;
        button.textContent = 'Copied!';
        setTimeout(() => {
          button.textContent = originalText;
        }, 1500);
      }
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  }, [formattedCode]);

  const handleDownload = useCallback(() => {
    if (!formattedCode) return;
    
    const blob = new Blob([formattedCode], { type: 'text/x-python' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'formatted_code.py';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [formattedCode]);

  const handleClear = useCallback(() => {
    setInputCode('');
    setFormattedCode('');
  }, []);

  const highlightPython = useCallback((code: string): string => {
    if (!code) return '';
    
    let highlighted = code;
    
    // Escape HTML
    highlighted = highlighted
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    
    // Highlight Python keywords
    pythonKeywords.forEach(keyword => {
      const regex = new RegExp(`\\b${keyword}\\b`, 'g');
      highlighted = highlighted.replace(regex, `<span class="text-purple-600 font-semibold">${keyword}</span>`);
    });
    
    // Highlight built-in functions
    builtinFunctions.forEach(func => {
      const regex = new RegExp(`\\b${func}\\b(?=\\s*\\()`, 'g');
      highlighted = highlighted.replace(regex, `<span class="text-blue-600 font-semibold">${func}</span>`);
    });
    
    // Highlight strings
    highlighted = highlighted.replace(/("""[\s\S]*?"""|'''[\s\S]*?''')/g, '<span class="text-green-600">$1</span>');
    highlighted = highlighted.replace(/("([^"\\]|\\.)*"|'([^'\\]|\\.)*')/g, '<span class="text-green-600">$1</span>');
    
    // Highlight numbers
    highlighted = highlighted.replace(/\b\d+(\.\d+)?(e[+-]?\d+)?\b/gi, '<span class="text-orange-600">$&</span>');
    
    // Highlight comments
    highlighted = highlighted.replace(/#.*$/gm, '<span class="text-gray-500 italic">$&</span>');
    
    // Highlight decorators
    highlighted = highlighted.replace(/(@\w+)/g, '<span class="text-yellow-600 font-semibold">$1</span>');
    
    // Highlight class and function definitions
    highlighted = highlighted.replace(/\b(class|def)\s+(\w+)/g, 
      '$1 <span class="text-cyan-600 font-bold">$2</span>');
    
    // Highlight self keyword
    highlighted = highlighted.replace(/\bself\b/g, '<span class="text-indigo-600 italic">self</span>');
    
    return highlighted;
  }, []);

  const exampleCode = `import os
import sys
from typing import List, Dict, Optional

class DataProcessor:
    """A class for processing and analyzing data."""
    
    def __init__(self, data_source: str):
        self.data_source = data_source
        self.processed_data = []
    
    def load_data(self) -> List[Dict]:
        """Load data from the specified source."""
        try:
            with open(self.data_source, 'r') as file:
                data = file.read()
                return self.parse_data(data)
        except FileNotFoundError:
            print(f"Error: File {self.data_source} not found")
            return []
    
    def parse_data(self, raw_data: str) -> List[Dict]:
        """Parse raw data into structured format."""
        lines = raw_data.strip().split('\\n')
        parsed = []
        
        for line in lines:
            if line.startswith('#'):
                continue
            
            parts = line.split(',')
            if len(parts) >= 3:
                record = {
                    'id': int(parts[0]),
                    'name': parts[1].strip(),
                    'value': float(parts[2])
                }
                parsed.append(record)
        
        return parsed
    
    @staticmethod
    def calculate_statistics(data: List[Dict]) -> Dict[str, float]:
        """Calculate basic statistics for the dataset."""
        if not data:
            return {}
        
        values = [item['value'] for item in data]
        return {
            'mean': sum(values) / len(values),
            'min': min(values),
            'max': max(values),
            'count': len(values)
        }

def main():
    processor = DataProcessor('data.csv')
    data = processor.load_data()
    
    if data:
        stats = DataProcessor.calculate_statistics(data)
        print("Data Statistics:")
        for key, value in stats.items():
            print(f"  {key}: {value:.2f}")
    else:
        print("No data to process")

if __name__ == "__main__":
    main()`;

  const countLines = (text: string): number => {
    return text ? text.split('\n').length : 0;
  };

  const countWords = (text: string): number => {
    return text ? text.trim().split(/\s+/).filter(word => word.length > 0).length : 0;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-blue-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Code className="w-8 h-8 text-white" />
                <div>
                  <h1 className="text-2xl font-bold text-white">Python Code Formatter</h1>
                  <p className="text-green-100">Format and beautify your Python code</p>
                </div>
              </div>
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Settings Panel */}
          {showSettings && (
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center space-x-2">
                  <label htmlFor="indent-size" className="text-sm font-medium text-gray-700">
                    Indent Size:
                  </label>
                  <select
                    id="indent-size"
                    value={indentSize}
                    onChange={(e) => setIndentSize(Number(e.target.value))}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={2}>2 spaces</option>
                    <option value={4}>4 spaces (PEP 8)</option>
                    <option value={8}>8 spaces</option>
                  </select>
                </div>
                
                <div className="flex items-center space-x-2">
                  <label htmlFor="line-length" className="text-sm font-medium text-gray-700">
                    Max Line Length:
                  </label>
                  <select
                    id="line-length"
                    value={maxLineLength}
                    onChange={(e) => setMaxLineLength(Number(e.target.value))}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={79}>79 (PEP 8)</option>
                    <option value={88}>88 (Black)</option>
                    <option value={100}>100</option>
                    <option value={120}>120</option>
                  </select>
                </div>
                
                <button
                  onClick={() => setInputCode(exampleCode)}
                  className="px-4 py-2 text-sm bg-blue-100 hover:bg-blue-200 text-blue-700 border border-blue-300 rounded-md transition-colors duration-200"
                >
                  Load Example
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 h-96 lg:h-[600px]">
            {/* Input Panel */}
            <div className="flex flex-col border-r border-gray-200">
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h2 className="font-semibold text-gray-700">Input Python Code</h2>
                <div className="flex space-x-2">
                  <button
                    onClick={handleClear}
                    className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded transition-colors duration-200"
                  >
                    Clear
                  </button>
                  <button
                    onClick={handleFormat}
                    disabled={isFormatting || !inputCode.trim()}
                    className="flex items-center space-x-1 px-3 py-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white text-sm rounded transition-colors duration-200"
                  >
                    {isFormatting ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Code className="w-4 h-4" />
                    )}
                    <span>{isFormatting ? 'Formatting...' : 'Format'}</span>
                  </button>
                </div>
              </div>
              <textarea
                value={inputCode}
                onChange={(e) => setInputCode(e.target.value)}
                placeholder="Paste your Python code here..."
                className="flex-1 p-4 font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-inset"
                spellCheck={false}
              />
            </div>

            {/* Output Panel */}
            <div className="flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h2 className="font-semibold text-gray-700">Formatted Python Code</h2>
                <div className="flex space-x-2">
                  <button
                    id="copy-button"
                    onClick={handleCopy}
                    disabled={!formattedCode}
                    className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-200 disabled:text-gray-400 disabled:hover:bg-transparent rounded transition-colors duration-200"
                  >
                    <Copy className="w-4 h-4" />
                    <span>Copy</span>
                  </button>
                  <button
                    onClick={handleDownload}
                    disabled={!formattedCode}
                    className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-200 disabled:text-gray-400 disabled:hover:bg-transparent rounded transition-colors duration-200"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download</span>
                  </button>
                </div>
              </div>
              <div className="flex-1 p-4 bg-gray-900 overflow-auto">
                {formattedCode ? (
                  <pre
                    className="font-mono text-sm text-gray-100 whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{
                      __html: highlightPython(formattedCode)
                    }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400">
                    <div className="text-center">
                      <Code className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Formatted Python code will appear here</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex flex-wrap items-center justify-between text-sm text-gray-600">
              <div className="flex items-center space-x-4">
                <span>Characters: {inputCode.length}</span>
                <span>Lines: {countLines(inputCode)}</span>
                <span>Words: {countWords(inputCode)}</span>
              </div>
              <div className="text-xs">
                Follows PEP 8 style guidelines • Supports Python 3.x syntax
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PythonFormatter;